import React, { useState } from 'react';

import { useApiClient } from '@eluve/api-client-provider';
import { type PatientImportJobStatus } from '@eluve/api-contract';
import {
  Button,
  Heading,
  Icon,
  LoadingIcon,
  Progress,
  Stack,
  Text,
  useToast,
} from '@eluve/components';

import { FlowStepper } from '../components/FlowStepper';
import { WhiteCard } from '../components/WhiteCard';
import { useImportJob } from '../hooks/useImportJob';
import { errMsg } from '../utils';

type Phase = 'parsing' | 'committing';

type StepState = 'done' | 'current' | 'pending';

type ChecklistStep = { label: string; state: StepState };

const PARSE_LABELS = [
  'Reading file',
  'Checking required columns',
  'Parsing rows',
  'Validating field formatting',
  'Looking up existing patient records',
] as const;

const PARSE_PHASES: Partial<
  Record<PatientImportJobStatus, { currentIdx: number; pct: number }>
> = {
  PARSING: { currentIdx: 2, pct: 40 },
  VALIDATING: { currentIdx: 3, pct: 80 },
};

const parseStateForStatus = (
  status: PatientImportJobStatus,
): { checklist: ChecklistStep[]; pct: number } => {
  const phase = PARSE_PHASES[status];
  const currentIdx = phase?.currentIdx ?? PARSE_LABELS.length;
  const pct = phase?.pct ?? 100;
  const checklist = PARSE_LABELS.map<ChecklistStep>((label, i) => ({
    label,
    state: i < currentIdx ? 'done' : i === currentIdx ? 'current' : 'pending',
  }));
  return { checklist, pct };
};

const MiniStepCircle: React.FC<{ state: StepState }> = ({ state }) => {
  if (state === 'done') {
    return (
      <div className="flex h-[18px] w-[18px] items-center justify-center rounded-full border border-border-positive-subtle bg-bg-positive-subtle">
        <Icon name="Check" size="xs" className="text-text-positive-subtle" />
      </div>
    );
  }
  if (state === 'current') {
    return (
      <div className="flex h-[18px] w-[18px] items-center justify-center rounded-full border border-border-tertiary bg-bg-primary">
        <div className="size-1.5 rounded-full bg-bg-accent-strong-secondary" />
      </div>
    );
  }
  return (
    <div className="h-[18px] w-[18px] rounded-full border border-border-tertiary bg-bg-secondary" />
  );
};

const TEXT_PROPS_BY_STATE: Record<
  StepState,
  { weight?: 'semibold'; color?: 'secondary' | 'tertiary' }
> = {
  done: { color: 'secondary' },
  current: { weight: 'semibold' },
  pending: { color: 'tertiary' },
};

const ChecklistRow: React.FC<{ step: ChecklistStep }> = ({ step }) => (
  <Stack direction="horizontal" gap={2} align="center">
    <MiniStepCircle state={step.state} />
    <Text size="sm" {...TEXT_PROPS_BY_STATE[step.state]}>
      {step.label}
    </Text>
  </Stack>
);

const useCancelImport = (tenantId: string, jobId: string) => {
  const apiClient = useApiClient();
  const toast = useToast();
  const [cancelling, setCancelling] = useState(false);

  const cancel = async () => {
    setCancelling(true);
    try {
      const res = await apiClient.patientImport.cancelImport({
        params: { tenantId, jobId },
        body: {},
      });
      if (res.status !== 200) {
        throw new Error(`cancelImport failed (${res.status})`);
      }
      toast.success('Cancellation requested');
    } catch (err) {
      toast.error(`Failed to cancel import: ${errMsg(err)}`);
    } finally {
      setCancelling(false);
    }
  };

  return { cancel, cancelling };
};

const ParsingScreen: React.FC<{ jobId: string; tenantId: string }> = ({
  jobId,
  tenantId,
}) => {
  const job = useImportJob(tenantId, jobId);
  const { cancel, cancelling } = useCancelImport(tenantId, jobId);
  const { checklist, pct } = parseStateForStatus(job.status);

  return (
    <Stack gap={6} align="stretch" className="mx-auto max-w-7xl">
      <Stack gap={1}>
        <Heading size="3xl" weight="semibold">
          Validating your file…
        </Heading>
        <Stack direction="horizontal" gap={2} align="center">
          <Icon name="FileText" size="xs" />
          <Text inline color="tertiary" size="sm" className="font-mono">
            {job.filename}
          </Text>
          <Text inline color="tertiary" size="sm">
            · just now
          </Text>
        </Stack>
      </Stack>

      <FlowStepper current="upload" />

      <Stack direction="horizontal" gap={2}>
        <Button variant="outline" onClick={cancel} disabled={cancelling}>
          <Icon name="CircleX" size="auto" />
          {cancelling ? 'Cancelling…' : 'Cancel import'}
        </Button>
      </Stack>

      <WhiteCard className="mx-auto max-w-xl p-6">
        <Stack gap={4}>
          <Stack direction="horizontal" gap={3} align="center">
            <LoadingIcon size="xl" />
            <Heading size="lg" weight="semibold">
              Reviewing data
            </Heading>
          </Stack>
          <Progress value={pct} className="h-1.5" />
          <Stack gap={2}>
            {checklist.map((step) => (
              <ChecklistRow key={step.label} step={step} />
            ))}
          </Stack>
        </Stack>
      </WhiteCard>
    </Stack>
  );
};

const CommittingScreen: React.FC<{ jobId: string; tenantId: string }> = ({
  jobId,
  tenantId,
}) => {
  const { cancel, cancelling } = useCancelImport(tenantId, jobId);
  return (
    <Stack gap={6} align="center" className="py-12">
      <LoadingIcon size="3xl" />
      <Stack gap={2} align="center">
        <Heading size="2xl" weight="semibold">
          Importing patients
        </Heading>
        <Text color="tertiary" className="max-w-md text-center">
          Writing rows to the database. This may take a moment for large files.
        </Text>
      </Stack>
      <Button variant="outline" onClick={cancel} disabled={cancelling}>
        <Icon name="CircleX" />
        {cancelling ? 'Cancelling…' : 'Stop remaining rows'}
      </Button>
      <Text color="disabled" size="xs">
        Stops processing remaining rows. Rows already imported will stay.
      </Text>
    </Stack>
  );
};

export const PatientImportProgressScreen: React.FC<{
  phase: Phase;
  jobId: string;
  tenantId: string;
}> = ({ phase, jobId, tenantId }) =>
  phase === 'parsing' ? (
    <ParsingScreen jobId={jobId} tenantId={tenantId} />
  ) : (
    <CommittingScreen jobId={jobId} tenantId={tenantId} />
  );
